import { getFollowerTemplate } from '@/data/followers';
import type { RecruitOutcome } from '@/schemas/character';
import { hasMoveSetup, type Move } from '@/schemas/move';
import { getPickCountForOutcome } from '@/services/followerCreation';
import { MoveChoiceGroupsField } from './MoveChoiceGroupsField';
import { MoveNarrativeFieldsField } from './MoveNarrativeFieldsField';
import { MoveRecruitOutcomeField } from './MoveRecruitOutcomeField';

/**
 * Editor di setup mossa per UNA singola mossa (FI-75). Sostituisce
 * `MoveSetupEditor` batch: oggi il setup viene mostrato inline dentro la
 * `<MoveCard setupSlot={...} />` invece che come sibling block sotto la
 * lista mosse. Allinea il flow editing (builder L1 + level-up) al pattern
 * già attivo in read-only su `FullSheetMoves` (Hub) dalla PR #93.
 *
 * Gestisce le 4 famiglie di setup:
 *  - shorthand single-group `choices` (es. Avvelenatore Ladro, Famiglio Mago)
 *  - multi-gruppo `choiceGroups` (es. Arma Prescelta Guerriero, Faro di
 *    Devozione Paladino, Divinità Chierico)
 *  - campi narrativi liberi `narrativeFields` (es. nome arma, descrizione
 *    patrono, Segno Rivelatore)
 *  - **D8a — recruitOutcome**: mosse con `FollowerTemplate.recruitRoll`
 *    (Druido Supporto Elementale, Sciamano Animare Zombi). Renderizza il
 *    `MoveRecruitOutcomeField` PRIMA del `MoveChoiceGroupsField` e calcola
 *    il `pickCount` runtime via `getPickCountForOutcome` come
 *    `pickCountOverrideByGroup` (gruppo `main`).
 *
 * **Filtro archetipo (FI-2 / FI-32)**: il filtro `isMoveChoiceAvailable`
 * agisce automaticamente dentro `MoveChoiceGroupsField` su
 * `option.requiresArchetype`. Le opzioni `freeForArchetype === archetypeId`
 * sono auto-aggiunte alle selezioni nel custom hook `useMoveSetupState` e
 * marcate "(gratis)" + non rimovibili nella UI.
 *
 * **Idempotenza render**: se la mossa non ha setup ({@link hasMoveSetup} =
 * false) il componente ritorna `null`. Permette al caller di passare
 * sempre `setupSlot={<MoveSetupPanel move={m} ... />}` senza pre-filtrare:
 * la card resta non-espandibile per descrizione-only.
 *
 * Le callback hanno `moveId` come primo argomento per compatibilità diretta
 * con `useMoveSetupState.handleChoiceGroupChange/...` (no closure per
 * callsite).
 */
export interface MoveSetupPanelProps {
  move: Move;
  /** Selezioni per gruppo. Gruppo virtuale `'main'` (shorthand) usa la
   * stessa mappa: il padre lo proietta da/verso `selectedChoices`. */
  selectedByGroup: Record<string, string[]>;
  narrativeValues: Record<string, string>;
  recruitOutcome: RecruitOutcome | undefined;
  archetypeId: string | undefined;
  onChoiceGroupChange: (moveId: string, groupId: string, selectedIds: string[]) => void;
  onNarrativeFieldChange: (moveId: string, fieldId: string, value: string) => void;
  /** **D8a** — opzionale. Quando l'esito cambia, il consumer deve resettare
   * `selectedChoices` del MoveRef (vedi `updateMoveRecruitOutcome`). */
  onRecruitOutcomeChange?: (moveId: string, outcome: RecruitOutcome) => void;
  /**
   * **FI-79 (2026-05-23)** — IDs delle mosse possedute dal PG, forwardato
   * al `MoveChoiceGroupsField` per disabilitare spell-options con
   * `Spell.availableTo[].requiresMove` non soddisfatto.
   * Opzionale: graceful fallback se omesso.
   */
  ownedMoveIds?: readonly string[] | undefined;
}

export function MoveSetupPanel({
  move,
  selectedByGroup,
  narrativeValues,
  recruitOutcome,
  archetypeId,
  onChoiceGroupChange,
  onNarrativeFieldChange,
  onRecruitOutcomeChange,
  ownedMoveIds,
}: MoveSetupPanelProps) {
  if (!hasMoveSetup(move)) return null;
  const followerTemplate = move.followerTemplateId
    ? getFollowerTemplate(move.followerTemplateId)
    : undefined;
  const recruitRoll = followerTemplate?.recruitRoll;
  const pickCountOverride = recruitRoll
    ? buildPickCountOverride(followerTemplate, recruitOutcome)
    : undefined;

  return (
    <div className="grid gap-3">
      {recruitRoll && onRecruitOutcomeChange && (
        <MoveRecruitOutcomeField
          moveId={move.id}
          recruitRoll={recruitRoll}
          value={recruitOutcome}
          onChange={(next) => onRecruitOutcomeChange(move.id, next)}
        />
      )}
      <MoveChoiceGroupsField
        move={move}
        selectedByGroup={selectedByGroup}
        archetypeId={archetypeId}
        ownedMoveIds={ownedMoveIds}
        onChange={(groupId, selectedIds) => onChoiceGroupChange(move.id, groupId, selectedIds)}
        pickCountOverrideByGroup={pickCountOverride}
      />
      <MoveNarrativeFieldsField
        move={move}
        values={narrativeValues}
        onChange={(fieldId, value) => onNarrativeFieldChange(move.id, fieldId, value)}
        selectedChoices={selectedByGroup.main ?? []}
        selectedChoiceGroups={selectedByGroup}
      />
    </div>
  );
}

/**
 * D8a — costruisce la mappa override `{ main: N }` per il gruppo principale
 * di una mossa con recruitRoll, derivando `N` dall'esito dichiarato. Ritorna
 * `undefined` se non c'è esito (la UI del `MoveChoiceGroupsField` userà il
 * `pickCount` statico della mossa-source che è 0 o predefinito).
 */
function buildPickCountOverride(
  template: NonNullable<ReturnType<typeof getFollowerTemplate>>,
  outcome: RecruitOutcome | undefined,
): Record<string, number> | undefined {
  const effectivePickCount = getPickCountForOutcome(template, outcome);
  if (effectivePickCount === undefined) return undefined;
  return { main: effectivePickCount };
}
